// CRM-ONBOARDING-TAB-2026-10-13: the top-level Onboarding tab -- everyone in onboarding at a glance. One row per link
// (onboarding_overview, migrations/2026-10-13-onboarding-overview.sql), the next step for staff derived in
// lib/onboardingOverview.js. "+ Add someone" opens the existing New onboarding link modal (App owns it); a row opens the
// candidate's existing Onboarding tab (OnboardingTab) in a side panel, where every action lives -- nothing is rebuilt.
// Rows carry no contact details; a Completed row shows its name and dates only.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OnboardingTab from './OnboardingTab';
import { RESTRICTED } from './lib/onboarding';
import { shapeOverview, filterRows, filterCounts, FILTERS, DEFAULT_FILTER, expiringSoonCount, examOnlyRows } from './lib/onboardingOverview';
import { byCandidate, examRow } from './lib/exams';
import { ExamChips, SendExamsButton, SendExamsModal } from './SendExams';
import { sentByText } from './lib/sendLink';
import OnboardingBoard from './OnboardingBoard';
import { boardRows } from './lib/onboardingBoard';
import { onboardLink } from './lib/onboarding';
import { examLink, EXAM_BY_ID } from './lib/exams';

const STYLE = `
.onbv-head { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.onbv-filters { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.onbv-row { display: grid; grid-template-columns: minmax(150px, 1.4fr) minmax(130px, 1fr) minmax(190px, 1.6fr) minmax(130px, 1fr) minmax(120px, 1fr);
  gap: 10px; align-items: center; padding: 10px 12px; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
.onbv-list .onbv-row:not(.onbv-colhead) { cursor: pointer; }
.onbv-list .onbv-row:not(.onbv-colhead):hover { background: #f8fafc; }
.onbv-colhead { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .03em; }
.onbv-cell-label { display: none; font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-right: 6px; }
/* ONBOARD-EXAMS-v2-2026-10-16: an Exams column once the exams migration is applied */
.onbv-row.with-exams { grid-template-columns: minmax(150px, 1.3fr) minmax(130px, 1fr) minmax(180px, 1.5fr) minmax(120px, .9fr) minmax(110px, .9fr) minmax(170px, 1.3fr); }
.onbv-drawer { position: fixed; top: 0; right: 0; bottom: 0; width: min(760px, 100vw); background: #f8fafc; z-index: 1000;
  box-shadow: -8px 0 24px #0f172a33; display: flex; flex-direction: column; }
@media (max-width: 760px) {
  .onbv-colhead { display: none; }
  .onbv-row, .onbv-row.with-exams { grid-template-columns: 1fr; gap: 4px; }
  .onbv-cell-label { display: inline; }
}
`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const pill = (bg, fg, border) => ({ display: 'inline-block', fontSize: 11, fontWeight: 600, background: bg, color: fg,
  border: `1px solid ${border || bg}`, borderRadius: 6, padding: '1px 6px', marginRight: 4, marginBottom: 2 });
const STEP_COLORS = {
  waiting: ['#f1f5f9', '#475569'], expiring: ['#fffbeb', '#92400e', '#fde68a'], stalled: ['#fffbeb', '#92400e', '#fde68a'],
  extend: ['#fef2f2', '#b91c1c', '#fecaca'],
  packet: ['#eff6ff', '#1d4ed8', '#bfdbfe'], sterling: ['#eff6ff', '#1d4ed8', '#bfdbfe'], adp: ['#eff6ff', '#1d4ed8', '#bfdbfe'],
  complete: ['#f0fdf4', '#15803d', '#bbf7d0'], expired: ['#f8fafc', '#64748b', '#e2e8f0'], revoked: ['#f8fafc', '#64748b', '#e2e8f0'],
  exams: ['#ecfeff', '#0e7490', '#a5f3fc'],
};
const check = (ok, label, key) => (
  <span key={key} data-testid={`onbv-progress-${key}`} data-done={ok ? 'yes' : 'no'} style={{ display: 'inline-block', marginRight: 8, whiteSpace: 'nowrap', color: ok ? '#15803d' : '#64748b' }}>
    {ok ? '✓' : '○'} {label}
  </span>
);
const Label = ({ children }) => <span className="onbv-cell-label">{children}</span>;

function linkText(r) {
  if (r.step === 'revoked') return `Sent ${fmtDate(r.created_at)} · revoked ${fmtDate(r.revoked_at)}`;
  if (r.status === 'complete' || r.completed_at) return `Sent ${fmtDate(r.created_at)} · finished ${fmtDate(r.completed_at)}`;
  if (r.step === 'expired' || r.step === 'extend') return `Sent ${fmtDate(r.created_at)} · expired ${fmtDate(r.expires_at)}`;
  const d = r.daysLeft;
  return `Sent ${fmtDate(r.created_at)} · expires ${fmtDate(r.expires_at)} (${d <= 0 ? 'today' : `${d} day${d === 1 ? '' : 's'} left`})`;
}

function Row({ r, onOpen, onOpenRecord, exams = null, examsApi = null, toast = () => {}, sentBy = '' }) {
  // EXAMS-SEND-2026-10-16: how this row's link went out (the timeline's send entries)
  const sent = sentBy ? <div data-testid={`onbv-sent-${r.session_id}`} style={{ fontSize: 11, color: '#0e7490', fontWeight: 600 }}>{sentBy}</div> : null;
  const [bg, fg, border] = STEP_COLORS[r.step] || STEP_COLORS.waiting;
  const name = (
    <span>
      <button className="nb" data-testid={`onbv-name-${r.session_id}`} title="Open their record"
        onClick={(e) => { e.stopPropagation(); onOpenRecord(r.candidate_id); }}
        style={{ fontWeight: 600, color: '#1d4ed8', textAlign: 'left', padding: 0 }}>{r.name}</button>
      {r.test && <span data-testid={`onbv-test-${r.session_id}`} style={{ ...pill('#f1f5f9', '#64748b', '#e2e8f0'), marginLeft: 6 }}>test</span>}
    </span>
  );
  // NIGHT-DIRECTSTAFF-2026-10-15 §4: the row opened only on a mouse click; its step chip is now a button, so the panel opens
  // from the keyboard too (Tab to it, Enter or Space)
  const stepButton = (
    <button className="nb" data-testid={`onbv-open-${r.session_id}`} aria-label={`Open onboarding for ${r.name}: ${r.stepText}`}
      onClick={(e) => { e.stopPropagation(); onOpen(r, e.currentTarget); }} style={{ padding: 0, textAlign: 'left' }}>
      <span data-testid={`onbv-step-${r.session_id}`} style={pill(bg, fg, border)}>{r.stepText}</span>
    </button>
  );
  const cls = exams ? 'onbv-row with-exams' : 'onbv-row';
  const examCell = exams ? (
    <div data-testid={`onbv-exams-${r.session_id}`}><Label>Exams</Label>
      <ExamChips rows={exams} api={examsApi} toast={toast} idPrefix={`onbv-exam-${r.session_id}`} /></div>
  ) : null;
  // ONBOARD-EXAMS-v2-2026-10-16: sent exams, no onboarding link
  if (r.step === 'exams') {
    return (
      <div className={cls} data-testid={`onbv-row-${r.session_id}`} data-step={r.step} onClick={() => onOpen(r)}>
        <div>{name}{r.discipline && <div style={{ fontSize: 12, color: '#64748b' }}>{r.discipline}</div>}</div>
        <div style={{ color: '#475569', fontSize: 12 }}><Label>Link</Label>Exams sent {fmtDate(r.created_at)}{sent}</div>
        <div style={{ color: '#475569', fontSize: 12 }}><Label>Progress</Label>No onboarding link</div>
        <div><Label>Next step</Label>{stepButton}</div>
        <div />
        {examCell}
      </div>
    );
  }
  // Completed: the name and the dates, nothing more
  if (r.step === 'complete') {
    return (
      <div className={cls} data-testid={`onbv-row-${r.session_id}`} data-step={r.step} onClick={() => onOpen(r)}>
        <div>{name}</div>
        <div style={{ color: '#475569' }}><Label>Link</Label>Sent {fmtDate(r.created_at)}{sent}</div>
        <div style={{ color: '#475569' }}><Label>Progress</Label>{r.completed_at ? `Finished ${fmtDate(r.completed_at)}` : ''}</div>
        <div><Label>Next step</Label>{stepButton}</div>
        <div style={{ color: '#475569' }}><Label>Entered in ADP</Label>{fmtDate(r.entered_in_adp_at)}</div>
        {examCell}
      </div>
    );
  }
  return (
    <div className={cls} data-testid={`onbv-row-${r.session_id}`} data-step={r.step} onClick={() => onOpen(r)}>
      <div>{name}{r.discipline && <div style={{ fontSize: 12, color: '#64748b' }}>{r.discipline}</div>}</div>
      <div style={{ color: '#475569', fontSize: 12 }}><Label>Link</Label>{linkText(r)}{sent}</div>
      <div style={{ fontSize: 12 }}>
        <Label>Progress</Label>
        {check(!!r.esign_consented_at, 'E-sign', 'esign')}
        {check(r.forms_signed >= 6, `Forms ${r.forms_signed}/6`, 'forms')}
        {check(!!r.i9_signed_at, 'I-9', 'i9')}
        {check(!!(r.background_signed_at && r.degree_saved_at), 'Background + degree', 'background')}
        {check(!!r.completed_at, 'Done', 'done')}
      </div>
      <div><Label>Next step</Label>{stepButton}</div>
      <div>
        <Label>Flags</Label>
        {r.flags.map((f) => <span key={f.key} data-testid={`onbv-flag-${f.key}-${r.session_id}`} style={pill('#fffbeb', '#92400e', '#fde68a')}>{f.text}</span>)}
      </div>
      {examCell}
    </div>
  );
}

/**
 * overview          { allowed, rows } | { error } | null (loading) -- App loads it (the nav badge needs it too)
 * onReload()        reload the overview;  onAdd() opens "New onboarding link";  onOpenRecord(candidateId);  onOpenExpirations()
 * focusCandidateId  open this candidate's panel (the modal's duplicate guard);  tabProps: what OnboardingTab needs
 */
// NIGHT-DIRECTSTAFF-2026-10-22 §4: List | Board -- the board (OnboardingBoard.jsx) needs loadDocuments() -> onboarding_documents
// rows { candidate_id, kind, uploaded_at } (null = not readable) and consentOf(candidate) for Remind's Text-first order.
export default function OnboardingView({ overview, candidates, onReload, onAdd, onOpenRecord, onOpenExpirations, expirationsApi,
  focusCandidateId = null, onFocusHandled = () => {}, tabProps, examsApi = null, sendApi = null, loadDocuments = null, loadHealth = null, consentOf = () => null }) {
  const [sentMap, setSentMap] = useState(null);       // EXAMS-SEND-2026-10-16: "exam:12" / "onboarding:34" -> channels
  const loadSent = useCallback(async () => { if (sendApi) setSentMap(await sendApi.sentChannels()); }, [sendApi]);
  useEffect(() => { loadSent(); }, [loadSent]);
  // ONBOARD-EXAMS-v2-2026-10-16: the exams, read beside the overview (dark -- ready:false -- until the migration)
  const [exams, setExams] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const loadExams = useCallback(async () => {
    if (!examsApi) return;
    try { setExams(await examsApi.load()); } catch (e) { setExams({ error: e.message }); }
  }, [examsApi]);
  useEffect(() => { loadExams(); }, [loadExams]);
  const examsReady = !!(exams && exams.ready);
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [search, setSearch] = useState('');
  const [panel, setPanel] = useState(null);           // candidate id whose Onboarding tab is open
  const opener = useRef(null);                        // what opened the panel: focus goes back there when it closes
  const closeRef = useRef(null);
  const openPanel = (candidateId, from = null) => { opener.current = from; setPanel(candidateId); };
  const closePanel = () => { setPanel(null); const back = opener.current; opener.current = null; if (back && back.focus) setTimeout(() => back.focus(), 0); };
  useEffect(() => { if (panel && closeRef.current) closeRef.current.focus(); }, [panel]);
  const [expiring, setExpiring] = useState(null);
  const [mode, setMode] = useState('list');            // §4 (10-22): 'list' | 'board'
  const [docs, setDocs] = useState(null);
  const [health, setHealth] = useState(null);         // DAY-DIRECTSTAFF-2026-10-22: the "required" ticks (null before the migration)
  useEffect(() => {
    let live = true;
    if (mode !== 'board' || !loadDocuments) return undefined;
    loadDocuments().then((d) => { if (live) setDocs(d); }).catch(() => { if (live) setDocs(null); });
    if (loadHealth) loadHealth().then((h) => { if (live) setHealth(h); }).catch(() => { if (live) setHealth(null); });   // 10-22
    return () => { live = false; };
  }, [mode, loadDocuments, loadHealth, overview]);

  useEffect(() => { onReload(); }, []);
  useEffect(() => {
    let live = true;
    if (!expirationsApi) return undefined;
    expirationsApi.load().then((r) => { if (live && r && r.allowed) setExpiring(expiringSoonCount(r.rows)); }).catch(() => {});
    return () => { live = false; };
  }, [expirationsApi]);
  useEffect(() => { if (focusCandidateId) { setPanel(focusCandidateId); onFocusHandled(); } }, [focusCandidateId]);
  useEffect(() => {
    if (!panel) return undefined;
    const esc = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [panel]);

  const allowed = !!(overview && overview.allowed);
  const rows = useMemo(() => {
    if (!allowed) return [];
    const linkRows = shapeOverview(overview.rows, candidates);
    return examsReady ? [...linkRows, ...examOnlyRows(linkRows, exams.links, candidates)] : linkRows;
  }, [allowed, overview, candidates, examsReady, exams]);
  const examsBy = useMemo(() => (examsReady ? byCandidate(exams.links, exams.attempts) : null), [examsReady, exams]);
  const examsFor = (r) => {
    if (!examsBy) return null;
    const got = examsBy.get(r.candidate_id) || { links: [], attempts: [] };
    return examRow(candidates.find((c) => c.id === r.candidate_id) || { specialty: r.discipline }, got.links, got.attempts);
  };
  const sentFor = (r) => {
    if (!sentMap) return '';
    if (r.step === 'exams') {
      const got = examsBy && examsBy.get(r.candidate_id);
      const newest = got && [...got.links].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
      return newest ? sentByText(sentMap.get(`exam:${newest.id}`)) : '';
    }
    return sentByText(sentMap.get(`onboarding:${r.session_id}`));
  };
  const board = useMemo(() => (allowed ? boardRows({ overviewRows: overview.rows, candidates, exams: examsReady ? exams : null, documents: docs, health }) : []),
    [allowed, overview, candidates, examsReady, exams, docs, health]);
  const remindPacket = async (row) => {
    const r = await tabProps.api.relink(row.session_id);
    await onReload();
    return { link: onboardLink(r.token, tabProps.config.baseUrl), linkId: r.session_id };
  };
  const remindExams = async (row) => {
    const got = (examsBy && examsBy.get(row.candidate_id)) || { links: [], attempts: [] };
    const done = new Set(got.attempts.filter((a) => a.submitted_at).map((a) => a.exam_id));
    const todo = [...new Set(got.links.filter((l) => !l.revoked_at).flatMap((l) => l.exams || []))].filter((id) => !done.has(id));
    if (!todo.length) throw new Error('Every exam they were sent is finished.');
    const r = await examsApi.send(row.candidate_id, todo, null);
    await loadExams();
    return { link: examLink(r.token, tabProps.config.baseUrl), linkId: r.link_id, names: todo.map((id) => (EXAM_BY_ID[id] ? EXAM_BY_ID[id].short : id)) };
  };
  const counts = useMemo(() => filterCounts(rows), [rows]);
  const shown = useMemo(() => filterRows(rows, filter, search), [rows, filter, search]);
  const panelCandidate = panel ? (candidates.find((c) => c.id === panel) || { id: panel, name: `Candidate #${panel}` }) : null;
  // 2026-10-14: the panel works on the candidate's newest link -- its session number heads the panel (rows are newest first)
  const panelSession = panel ? rows.find((r) => r.candidate_id === panel) : null;

  return (
    <div className="fade-in phone-tap" data-testid="onboarding-view">
      <style>{STYLE}</style>
      <div className="onbv-head">
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>Onboarding</h2>
          <button className="nb" data-testid="onbv-expiring" onClick={onOpenExpirations}
            style={{ fontSize: 12, color: '#1d4ed8', padding: 0, marginTop: 2 }}>
            Expiring soon{expiring === null ? '' : ` (${expiring})`} →
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {examsApi && <SendExamsButton ready={examsReady && allowed} onOpen={() => setSendOpen(true)} />}
        <button className="nb" data-testid="onbv-add" onClick={onAdd} disabled={overview && overview.allowed === false}
          style={{ background: '#0f766e', color: 'white', borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 700,
            opacity: overview && overview.allowed === false ? 0.45 : 1 }}>
          ＋ Add someone
        </button>
        </div>
      </div>

      {overview && overview.allowed === false && <p data-testid="onbv-restricted" style={{ fontSize: 13, color: '#92400e' }}>{RESTRICTED}</p>}
      {overview && overview.error && <p data-testid="onbv-error" style={{ fontSize: 13, color: '#b91c1c' }}>Could not load onboarding: {overview.error}</p>}
      {!overview && <p style={{ fontSize: 13, color: '#64748b' }}>Loading…</p>}

      {allowed && rows.length === 0 && (
        <div data-testid="onbv-empty" className="card" style={{ padding: 28, textAlign: 'center' }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 6px' }}>No one is onboarding yet.</p>
          <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>Add someone to make their onboarding link. They show up here as they go.</p>
          <button className="nb" data-testid="onbv-empty-add" onClick={onAdd}
            style={{ background: '#0f766e', color: 'white', borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 700 }}>＋ Add someone</button>
        </div>
      )}

      {allowed && rows.length > 0 && (
        <div role="group" aria-label="Show as" data-testid="onbv-mode" style={{ display: 'flex', gap: 6, margin: '4px 0 10px' }}>
          {[['list', 'List'], ['board', 'Status board']].map(([k, label]) => (
            <button key={k} className="nb" data-testid={`onbv-mode-${k}`} aria-pressed={mode === k} onClick={() => setMode(k)}
              style={{ borderRadius: 20, padding: '0 16px', minHeight: 44, fontSize: 13, fontWeight: 700, background: mode === k ? '#0f2744' : '#f1f5f9', color: mode === k ? 'white' : '#334155' }}>{label}</button>
          ))}
        </div>
      )}
      {allowed && rows.length > 0 && mode === 'board' && (
        <OnboardingBoard rows={board} candidates={candidates} remindPacket={remindPacket} remindExams={remindExams} sendApi={sendApi} consentOf={consentOf}
          onOpen={(r) => onOpenRecord(r.candidate_id)} toast={tabProps.toast} />
      )}
      {allowed && rows.length > 0 && mode === 'list' && (
        <>
          <div className="onbv-filters">
            {FILTERS.map(([k, label]) => (
              <button key={k} className="nb" data-testid={`onbv-filter-${k}`} data-count={counts[k]} onClick={() => setFilter(k)}
                style={{ borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 600,
                  background: filter === k ? '#1d4ed8' : '#f1f5f9', color: filter === k ? 'white' : '#475569' }}>
                {label} <span style={{ opacity: 0.8 }}>{counts[k]}</span>
              </button>
            ))}
          </div>
          <input className="input" data-testid="onbv-search" placeholder="Search by name" value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320, width: '100%', fontSize: 13, marginBottom: 10 }} />
          <div className="card onbv-list" data-testid="onbv-list" style={{ padding: 0, overflow: 'hidden' }}>
            <div className={`onbv-row onbv-colhead${examsReady ? ' with-exams' : ''}`}><div>Candidate</div><div>Link</div><div>Progress</div><div>Next step</div><div>Flags</div>{examsReady && <div>Exams</div>}</div>
            {shown.map((r) => <Row key={r.session_id} r={r} onOpen={(x, from) => openPanel(x.candidate_id, from)} onOpenRecord={onOpenRecord}
              exams={examsFor(r)} examsApi={examsApi} toast={tabProps && tabProps.toast} sentBy={sentFor(r)} />)}
            {shown.length === 0 && <p data-testid="onbv-none" style={{ fontSize: 13, color: '#64748b', padding: 16, margin: 0 }}>Nobody here right now.</p>}
          </div>
        </>
      )}

      {sendOpen && (
        <SendExamsModal candidates={candidates} api={examsApi} config={tabProps && tabProps.config} toast={tabProps && tabProps.toast}
          onSent={() => { loadExams(); loadSent(); }} onClose={() => setSendOpen(false)} sendApi={sendApi} />
      )}

      {panelCandidate && (
        <>
          <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: '#0f172a55', zIndex: 999 }} />
          <aside className="onbv-drawer" data-testid="onbv-panel" role="dialog" aria-label={`Onboarding: ${panelCandidate.name}`}>
            {/* the right edge stays clear: AuthGate's Sign Out sits fixed at the top right, above everything */}
            <div style={{ padding: '10px 104px 12px 16px', background: 'white', borderBottom: '1px solid #e2e8f0' }}>
              <button className="nb" ref={closeRef} data-testid="onbv-panel-close" onClick={closePanel}
                style={{ fontSize: 13, fontWeight: 600, color: '#475569', padding: '2px 0', marginBottom: 4 }}>← Back to list</button>
              <div data-testid="onbv-panel-session" style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '.02em' }}>
                {panelSession ? `Session ${panelSession.session_id}` : 'No onboarding link yet'}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{panelCandidate.name}</div>
              <button className="nb" data-testid="onbv-panel-record" onClick={() => { setPanel(null); onOpenRecord(panelCandidate.id); }}
                style={{ fontSize: 12, color: '#1d4ed8', padding: 0 }}>Open their record →</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              <OnboardingTab key={panelCandidate.id} candidate={panelCandidate} onChanged={() => { onReload(); loadExams(); loadSent(); }} {...tabProps} examsApi={examsApi} sendApi={sendApi} />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
